import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  trackCompareFlightTooltipAboutClick,
  trackCompareFlightTooltipShow,
} from '../../CompareBannerContainer/tracking';
import { CompareFlightModalContainer } from '../../CompareFlightModalContainer';
import { getIsCompareFlightTooltipShow } from '../../../selectors/compareFlightTooltip';
import { useApplicationContext } from '../../../utils/applicationContext';
import { COMPARE_ENABLED_CONFIG, COMPARE_FLIGHT_TRAP_ENABLED_CONFIG } from '../constants/compare';
import { CompareButtonOnboarding } from '../CompareButtonOnboarding';
import { CompareButtonWithTooltip } from '../CompareButtonWithTooltip';
import { CompareFlightButtonOnboarding, useCompareFlightOnboarding } from '../CompareFlightOnboardingTooltip';
import { useCompareOnboarding } from '../hooks/useCompareOnboarding';

export interface ICompareButtonWithOnboardingContainerProps {
  isAdded: boolean;
  isVisible: boolean;
  onClick(): void;
  withOnboarding: boolean;
}

export const CompareButtonWithOnboardingContainer = ({
  isAdded,
  isVisible,
  onClick,
  withOnboarding,
}: ICompareButtonWithOnboardingContainerProps) => {
  const { config } = useApplicationContext();
  const isCompareFlightTooltipShow = useSelector(getIsCompareFlightTooltipShow);
  const [compareFlightModalOpen, setCompareFlightModalOpen] = React.useState(false);
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const isCompareFlightEnabled = config.get<boolean>(COMPARE_FLIGHT_TRAP_ENABLED_CONFIG) || false;

  const isCompareFlightOnboardingAvailable = withOnboarding && isCompareFlightEnabled && isCompareFlightTooltipShow;
  const { open, closeHandler } = useCompareOnboarding(
    withOnboarding && isCompareEnabled && !isCompareFlightOnboardingAvailable,
  );
  const { open: compareFlightOpen, closeHandler: compareFlightCloseHandler } = useCompareFlightOnboarding(
    isCompareFlightOnboardingAvailable,
  );

  React.useEffect(() => {
    if (compareFlightOpen && isVisible) {
      trackCompareFlightTooltipShow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareFlightOpen]);

  const handleCompareFlightModalOpen = React.useCallback(() => {
    trackCompareFlightTooltipAboutClick();
    compareFlightCloseHandler();
    setCompareFlightModalOpen(true);
  }, [compareFlightCloseHandler]);

  const handleCompareFlightModalClose = React.useCallback(() => {
    setCompareFlightModalOpen(false);
  }, []);

  if (!isVisible) {
    return null;
  }

  const button = <CompareButtonWithTooltip isAdded={isAdded} onClick={onClick} />;

  const buttonWithOnboarding = !withOnboarding ? (
    button
  ) : compareFlightOpen ? (
    <CompareFlightButtonOnboarding
      open={compareFlightOpen}
      onClose={compareFlightCloseHandler}
      onAboutComparisonClick={handleCompareFlightModalOpen}
    >
      {button}
    </CompareFlightButtonOnboarding>
  ) : open ? (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      {button}
    </CompareButtonOnboarding>
  ) : (
    button
  );

  return (
    <>
      {buttonWithOnboarding}
      {compareFlightModalOpen && <CompareFlightModalContainer onClose={handleCompareFlightModalClose} />}
    </>
  );
};
